import { useMemo, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { PayrollConceptFragment } from 'generated/resources/graphql'

import Selector from 'modules/core/components/Selector'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import type {
  LabelMappedWithAI,
  LabelToConceptId,
} from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/types'

import ConceptModal from './ConceptModal'
import NewConceptBox from './NewConceptBox'

interface Props {
  label: string
  concepts: PayrollConceptFragment[]
  readonly: boolean
  labelToConceptId: LabelToConceptId
  labelMappedWithAI: LabelMappedWithAI
  onUpdateLabelToConceptId: (labelToConceptId: LabelToConceptId) => void
  onUpdateLabelMappedWithAI: (labelMappedWithAI: LabelMappedWithAI) => void
}

const ConceptSelectorForLabel = ({
  label,
  concepts,
  readonly,
  labelToConceptId,
  labelMappedWithAI,
  onUpdateLabelToConceptId,
  onUpdateLabelMappedWithAI,
}: Props) => {
  const [showConceptModal, setShowConceptModal] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)

  const mapLabelToConceptId = (conceptLabel: string, conceptId: number | undefined) => {
    onUpdateLabelMappedWithAI({ ...labelMappedWithAI, [conceptLabel]: false })
    onUpdateLabelToConceptId({ ...labelToConceptId, [conceptLabel]: conceptId })
  }

  const sortedConcepts = useMemo(() => {
    // TODO: concept labels and descriptions should be processed at backend
    // when requesting the concepts. Name should be the key.
    return concepts
      .map((concept) => ({
        ...concept,
        name: concept.label,
        description: concept.description,
      }))
      .sort((a, b) => {
        // Make sure that if they are selected, they are at the top
        if (a.id === labelToConceptId[label]) return -1
        if (b.id === labelToConceptId[label]) return 1

        return a.name.localeCompare(b.name)
      })
  }, [concepts, labelToConceptId, label])

  return (
    <>
      <Selector.Popover
        isDisabled={readonly}
        placeholder={i18n.t('common.select')}
        blankLabel={i18n.t('common.empty')}
        searchPlaceholder={i18n.t('common.search')}
        onChange={(value) => {
          // `value` will be null if the clicked option is the one already selected
          mapLabelToConceptId(label, value ? Number(value) : undefined)
        }}
        value={[labelToConceptId[label]]}
        footer={
          <NewConceptBox
            onCreate={() => {
              setEditMode(false)
              setShowConceptModal(true)
              track(trackingEvents.payroll.openAddNewConceptModal)
            }}
            onEdit={() => {
              setEditMode(true)
              setShowConceptModal(true)
            }}
          />
        }
        icon={labelToConceptId[label] && labelMappedWithAI[label] ? 'ai' : undefined}
      >
        {sortedConcepts.map((sortedConcept) => {
          const conceptNotFoundInLabel =
            Object.values(labelToConceptId).includes(sortedConcept.id) &&
            labelToConceptId[label] !== sortedConcept.id

          return (
            <Selector.Option
              key={sortedConcept.id}
              value={sortedConcept.id}
              label={sortedConcept.name || ''}
              searchValue={sortedConcept.name || ''}
              isDisabled={conceptNotFoundInLabel}
              showSeparator={false}
            >
              <Box
                width="full"
                flexDirection="row"
                justifyContent="spaceBetween"
                overflowY="hidden"
                position="relative"
              >
                <Text.Normal weight="medium" ellipsis>
                  {sortedConcept.name}
                </Text.Normal>
                {labelToConceptId[label] === sortedConcept.id && <Icon.Medium icon="check" />}
              </Box>
            </Selector.Option>
          )
        })}
      </Selector.Popover>
      {showConceptModal && (
        <ConceptModal
          onClose={() => {
            setShowConceptModal(false)
          }}
          onCreate={(conceptId) => mapLabelToConceptId(label, conceptId)}
          sortedConcepts={sortedConcepts}
          editMode={editMode}
        />
      )}
    </>
  )
}

export default ConceptSelectorForLabel
